import { useState } from 'react';
import { MousePointerClick, Sparkles } from 'lucide-react';
import type { TeamMemberCardDto, TeamSearchResultDto } from '../../../domain/contracts/api.contracts';
import { TierLevel, TIER_DESIGNATION_MAP } from '../../../domain/enums/TierLevel';
import { useCurrentUser } from '../../../application/hooks/useCurrentUser';
import { useMediaQuery } from '../../../application/hooks/useMediaQuery';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { useTeamUrlState } from '../../../application/hooks/team/useTeamUrlState';
import { DEFAULT_TEAM_FILTERS, useTeamLevel, type TeamFilters } from '../../../application/hooks/team/useTeamQueries';
import { BottomSheet } from '../../components/ui/BottomSheet';
import { TeamBreadcrumb } from './navigator/TeamBreadcrumb';
import { BranchKpiStrip } from './navigator/BranchKpiStrip';
import { TeamFilterBar } from './navigator/TeamFilterBar';
import { TeamLevelList } from './navigator/TeamLevelList';
import { TeamSearch } from './navigator/TeamSearch';
import { Member360Panel } from './member360/Member360Panel';
import '../pages.css';
import './TeamPage.css';

/**
 * W05 — Team Navigator & Member 360 (PRD v1.6.0 §7.5.1). One hierarchy level at a time with
 * a breadcrumb trail; selecting any member opens their profile + installments. Desktop is
 * master/detail; mobile opens the panel as a full-height bottom sheet. State lives in the URL.
 */
export default function TeamPage() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const nav = useTeamUrlState();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [filters, setFilters] = useState<TeamFilters>(DEFAULT_TEAM_FILTERS);
  const level = useTeamLevel(nav.focus, filters);
  const first = level.data?.pages[0];

  const viewerTier = user?.tier ?? TierLevel.L2;
  const filtered = filters.tier !== 'ALL' || filters.status !== 'ALL';

  const focusOn = (userId: string | null) => {
    setFilters(DEFAULT_TEAM_FILTERS);
    nav.setFocus(userId, isDesktop);
  };
  const onCrumb = (index: number) => focusOn(index === 0 ? null : (first?.path[index]?.apartmentId ?? null));
  const onDrill = (card: TeamMemberCardDto) => focusOn(card.apartmentId);
  const onPick = (r: TeamSearchResultDto) => {
    setFilters(DEFAULT_TEAM_FILTERS);
    nav.jumpTo(r.path.length <= 1 ? null : r.path[r.path.length - 1].apartmentId, r.card.apartmentId);
  };

  const panel = nav.member ? (
    <Member360Panel
      key={nav.member}
      userId={nav.member}
      tab={nav.tab}
      onTab={nav.setTab}
      onOpenMember={nav.openMember}
      onFocusMember={(userId) => focusOn(userId)}
    />
  ) : null;

  return (
    <div className="tm-root" data-testid="team-page">
      <header className="tm-header">
        <div>
          <h1 className="tm-title">{t('team.title')}</h1>
          <p className="tm-subtitle">{t('team.subtitle')}</p>
        </div>
        <span className={`tm-rank${viewerTier === TierLevel.L6 ? ' is-director' : ''}`}>
          <Sparkles size={13} aria-hidden /> {viewerTier} · {TIER_DESIGNATION_MAP[viewerTier]}
        </span>
      </header>

      <TeamSearch onPick={onPick} />
      <TeamBreadcrumb path={first?.path ?? []} onSelect={onCrumb} />
      <BranchKpiStrip summary={first?.summary} />

      <div className={`tm-workspace${isDesktop ? ' is-split' : ''}`}>
        <section className="tm-nav-pane" aria-label={t('team.title')}>
          <TeamFilterBar filters={filters} onChange={setFilters} showTierPills={viewerTier !== TierLevel.L2} />
          <TeamLevelList
            level={level}
            selectedUserId={nav.member}
            filtered={filtered}
            onOpen={(card) => nav.openMember(card.apartmentId)}
            onDrill={onDrill}
            onResetFilters={() => setFilters(DEFAULT_TEAM_FILTERS)}
            onBackToRoot={() => focusOn(null)}
          />
        </section>

        {isDesktop ? (
          <aside className="tm-detail-pane">
            {panel ?? (
              <div className="tm-detail-empty" data-testid="member360-empty">
                <MousePointerClick size={22} aria-hidden />
                <p>{t('team.selectHint')}</p>
              </div>
            )}
          </aside>
        ) : (
          <BottomSheet open={Boolean(nav.member)} title={t('team.tabProfile')} onClose={nav.closeMember} testId="member360-sheet">
            {panel}
          </BottomSheet>
        )}
      </div>
    </div>
  );
}
